import { ThemeProvider, useTheme } from "@mui/material";
import { createElement, useEffect, useRef, useMemo, useState, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { useGetShopsQuery } from "../../../../api/shopsApi";
import { Shop } from "../../../../types/shop";
import ShopMarker from "../../components/map/ShopMarker";

interface UseShopMarkersProps {
  map: google.maps.Map | null;
  showShops: boolean;
  searchQuery?: string;
  googleShops?: Shop[]; // Add Google shops data
  onShopClick?: (shop: Shop) => void; // Add click handler
  activeShopId?: number; // Add active shop ID for highlighting
}

export const useShopMarkers = ({
  map,
  showShops,
  searchQuery = "",
  googleShops = [],
  onShopClick,
  activeShopId,
}: UseShopMarkersProps) => {
  const theme = useTheme();
  const markersRef = useRef<
    Map<number, google.maps.marker.AdvancedMarkerElement>
  >(new Map());
  const rootsRef = useRef<Map<number, any>>(new Map());
  const markerLibraryRef = useRef<
    typeof google.maps.marker.AdvancedMarkerElement | null
  >(null);
  const [isMarkerLibraryLoaded, setIsMarkerLibraryLoaded] = useState(false);
  
  // Get all shops (both trusted and DNU) and filter by isActive on frontend
  const { data: allShops = [] } = useGetShopsQuery(undefined);
  const shops = useMemo(
    () => allShops.filter((shop) => shop.isActive),
    [allShops]
  );

  // Load the marker library
  const loadMarkerLibrary = useCallback(async () => {
    if (!markerLibraryRef.current) {
      try {
        const { AdvancedMarkerElement } = (await google.maps.importLibrary(
          "marker"
        )) as google.maps.MarkerLibrary;
        markerLibraryRef.current = AdvancedMarkerElement;
        setIsMarkerLibraryLoaded(true);
      } catch (error) {
        console.error("Failed to load marker library:", error);
        return null;
      }
    }
    return markerLibraryRef.current;
  }, []);

  // Load marker library on mount
  useEffect(() => {
    if (map && !isMarkerLibraryLoaded) {
      loadMarkerLibrary();
    }
  }, [map, isMarkerLibraryLoaded, loadMarkerLibrary]);

  useEffect(() => {
    console.log("🏪 useShopMarkers effect:", {
      hasMap: !!map,
      hasShops: !!shops,
      showShops,
      googleShopsCount: googleShops.length,
      isMarkerLibraryLoaded,
    });

    if (!map || !shops || !isMarkerLibraryLoaded || !markerLibraryRef.current) return;

    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current.clear();
    rootsRef.current.clear();

    if (!showShops) {
      console.log("🚫 Markers hidden - showShops is false");
      return;
    }

    // Combine trusted shops and Google shops
    const allShops = [...(shops || []), ...googleShops];

    const filteredShops = searchQuery.trim()
      ? allShops.filter(
          (shop) =>
            shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            shop.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            shop.description?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : allShops;

    const AdvancedMarkerElement = markerLibraryRef.current;

    filteredShops.forEach((shop) => {
      if (!shop.latitude || !shop.longitude) return;

      const container = document.createElement("div");
      const root = createRoot(container);
      rootsRef.current.set(shop.id, root);

      const marker = new AdvancedMarkerElement({
        map,
        position: { lat: shop.latitude, lng: shop.longitude },
        content: container,
      });

      // Add click listener for map centering
      marker.addListener("click", () => {
        if (onShopClick) {
          onShopClick(shop);
        }
        // Center map on shop
        if (map) {
          map.setCenter({ lat: shop.latitude, lng: shop.longitude });
          map.setZoom(15);
        }
      });

      root.render(
        createElement(
          ThemeProvider,
          { theme },
          createElement(ShopMarker, {
            shop,
            isActive: activeShopId === shop.id,
            onShopPress: onShopClick,
          })
        )
      );

      markersRef.current.set(shop.id, marker);
    });

    if (filteredShops.length > 0 && searchQuery.trim()) {
      const firstShop = filteredShops[0];
      if (firstShop.latitude && firstShop.longitude) {
        map.setZoom(15);
        map.setCenter({ lat: firstShop.latitude, lng: firstShop.longitude });
      }
    }
  }, [
    map,
    shops,
    googleShops,
    showShops,
    searchQuery,
    theme,
    onShopClick,
    activeShopId,
    isMarkerLibraryLoaded,
  ]);

  return null;
};
